import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Select from 'flarum/common/components/Select';
import BelgiumMap from './BelgiumMap';
import { t, tt, route, find, syncLocale, label, formatNumber, intlLocale } from '../i18n';

const PROVINCES = [
  'Antwerpen',
  'Limburg',
  'Oost-Vlaanderen',
  'Vlaams-Brabant',
  'West-Vlaanderen',
  'Brussel',
  'Henegouwen',
  'Luik',
  'Luxemburg',
  'Namen',
  'Waals-Brabant',
];

const SORTS = ['name', 'popular', 'oldest'];
const DECADES = [1960, 1970, 1980, 1990, 2000, 2010];

/**
 * /clubs — het clubarchief: kaart, filters en clubkaarten.
 */
export default class ClubsPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    syncLocale();

    this.loading = true;
    this.clubs = [];
    this.query = '';
    this.province = '';
    this.decade = '';
    this.sort = 'name';

    app.history.push('lc.clubs', tt('clubs.title'));
    app.setTitle(tt('clubs.title'));
    app.setTitleCount(0);

    // Bij server-side preload staan de clubs al in de store.
    const preloaded = app.store.all('lc-clubs');
    if (preloaded.length > 20) {
      this.clubs = preloaded;
      this.loading = false;
    }

    find('lc-clubs').then((clubs) => {
      this.clubs = clubs;
      this.loading = false;
      m.redraw();
    });
  }

  view() {
    const belgian = this.clubs.filter((c) => !c.isAbroad());
    const abroad = this.clubs.filter((c) => c.isAbroad());
    const filtered = this.sorted(this.filter(belgian));
    const totals = this.totals();
    const stats = app.forum.attribute('lcStats') || {};

    return (
      <div className="LcClubsPage">
        <section className="LcHero">
          <div className="container LcHero-inner">
            <div className="LcHero-text">
              <p className="LcEyebrow">The Real Clubhistory</p>
              <h1 className="LcHero-title">{t('home.hero_title', { span: <span /> })}</h1>
              <p className="LcHero-lead">{t('home.hero_lead', { clubs: totals.clubs, posts: formatNumber(stats.posts) })}</p>
              <div className="LcHero-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  className="FormControl"
                  type="search"
                  placeholder={tt('clubs.search_placeholder')}
                  value={this.query}
                  oninput={(e) => (this.query = e.target.value)}
                  aria-label={tt('clubs.search_placeholder')}
                />
              </div>
              <dl className="LcStats">
                <div>
                  <dt>{t('home.stat_clubs')}</dt>
                  <dd>{totals.clubs}</dd>
                </div>
                <div>
                  <dt>{t('home.stat_posts')}</dt>
                  <dd>{formatNumber(stats.posts)}</dd>
                </div>
                <div>
                  <dt>{t('home.stat_members')}</dt>
                  <dd>{formatNumber(stats.users)}</dd>
                </div>
                <div>
                  <dt>{t('home.stat_since')}</dt>
                  <dd>{stats.firstYear || 2004}</dd>
                </div>
              </dl>
            </div>
            <div className="LcHero-map">
              {this.loading ? (
                <LoadingIndicator />
              ) : (
                <BelgiumMap clubs={filtered} province={this.province} onselect={(club) => this.open(club)} />
              )}
            </div>
          </div>
        </section>

        {this.isFiltering() ? null : this.recent()}

        <section className="container LcArchive">
          <div className="LcFilters">
            <Select
              value={this.province}
              options={{ '': tt('clubs.all_provinces'), ...Object.fromEntries(PROVINCES.map((p) => [p, label('provinces', p)])) }}
              onchange={(value) => (this.province = value)}
            />
            <Select
              value={this.decade}
              options={{ '': tt('clubs.all_decades'), ...Object.fromEntries(DECADES.map((d) => [d, tt('common.decade', { decade: String(d).slice(2), year: d })])) }}
              onchange={(value) => (this.decade = value)}
            />
            <Select value={this.sort} options={Object.fromEntries(SORTS.map((k) => [k, tt(`common.sort.${k}`)]))} onchange={(value) => (this.sort = value)} />
            <span className="LcFilters-count">{t('home.count', { count: filtered.length })}</span>
          </div>

          {this.loading ? (
            <LoadingIndicator />
          ) : filtered.length ? (
            <ul className="LcGrid">{filtered.map((club) => this.card(club))}</ul>
          ) : (
            <p className="LcEmpty">{t('clubs.no_results')}</p>
          )}

          {abroad.length && !this.province ? (
            <div className="LcAbroad">
              <h2>{t('clubs.abroad')}</h2>
              <p>{t('clubs.abroad_intro')}</p>
              <ul className="LcGrid LcGrid--small">{this.sorted(this.filter(abroad, true)).map((club) => this.card(club))}</ul>
            </div>
          ) : null}
        </section>
      </div>
    );
  }

  /** Laatst toegevoegd: de nieuwste clubs, artiesten en figuren. */
  recent() {
    const recent = app.forum.attribute('lcRecent');
    if (!recent) return null;

    const columns = [
      { key: 'clubs', route: 'lc.club', all: 'lc.clubs', icon: 'fas fa-map-marker-alt', extra: (v) => v },
      { key: 'artists', route: 'lc.artist', all: 'lc.artists', icon: 'fas fa-compact-disc', extra: (v) => v && tt(`artist.type.${v}`) },
      { key: 'figures', route: 'lc.figure', all: 'lc.figures', icon: 'fas fa-user', extra: (v) => v && tt(`figure.type.${v}`) },
    ].filter((c) => (recent[c.key] || []).length);
    if (!columns.length) return null;

    const date = new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short' });

    return (
      <section className="container LcRecent">
        <h2 className="LcRecent-title">{t('home.recent.title')}</h2>
        <div className="LcRecent-columns">
          {columns.map((c) => (
            <div className="LcRecent-column">
              <h3>
                <Link href={route(c.all)}>{t(`home.recent.${c.key}`)}</Link>
              </h3>
              <ul>
                {recent[c.key].map((item) => (
                  <li key={item.slug}>
                    <Link href={route(c.route, { slug: item.slug })}>
                      <span className="LcRecent-name">{item.name}</span>
                      <span className="LcRecent-meta">
                        {item.extra ? [<i className={c.icon} aria-hidden="true" />, ' ', c.extra(item.extra)] : null}
                      </span>
                    </Link>
                    {item.added ? <time dateTime={item.added}>{date.format(new Date(item.added))}</time> : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    );
  }

  card(club) {
    const genres = (club.genres() || []).slice(0, 3);
    return (
      <li key={club.slug()} className={'LcCard' + (club.isFeatured() ? ' LcCard--featured' : '')}>
        <Link href={route('lc.club', { slug: club.slug() })} className="LcCard-link">
          <span className="LcCard-years">{club.years() || t('clubs.unknown_years')}</span>
          <h3 className="LcCard-name">{club.name()}</h3>
          <span className="LcCard-place">
            <i className="fas fa-map-marker-alt" aria-hidden="true" /> {club.city() || '—'}
            {club.isAbroad() ? ` (${club.country()})` : ''}
          </span>
          {genres.length ? (
            <span className="LcCard-genres">
              {genres.map((g) => (
                <span className="LcChip">{g}</span>
              ))}
            </span>
          ) : null}
          <span className="LcCard-meta">
            <i className="far fa-comments" aria-hidden="true" /> {t('clubs.discussions', { count: club.discussionCount() || 0 })}
          </span>
        </Link>
      </li>
    );
  }

  isFiltering() {
    return !!(this.query.trim() || this.province || this.decade);
  }

  filter(clubs, ignoreProvince = false) {
    const q = this.query.trim().toLowerCase();
    return clubs.filter((club) => {
      if (q && !club.searchText().includes(q)) return false;
      if (!ignoreProvince && this.province && club.province() !== this.province) return false;
      if (this.decade && !club.decades().includes(Number(this.decade))) return false;
      return true;
    });
  }

  sorted(clubs) {
    const list = clubs.slice();
    if (this.sort === 'name') list.sort((a, b) => a.name().localeCompare(b.name(), 'nl'));
    if (this.sort === 'oldest') list.sort((a, b) => (a.opened() || 9999) - (b.opened() || 9999));
    if (this.sort === 'popular') list.sort((a, b) => (b.postCount() || 0) - (a.postCount() || 0));
    return list;
  }

  totals() {
    return this.clubs.reduce(
      (acc, c) => ({
        clubs: acc.clubs + 1,
        discussions: acc.discussions + (c.discussionCount() || 0),
        posts: acc.posts + (c.postCount() || 0),
      }),
      { clubs: 0, discussions: 0, posts: 0 }
    );
  }

  open(club) {
    m.route.set(route('lc.club', { slug: club.slug() }));
  }
}
